import { gsap } from 'gsap';
import {
  cleanupPage,
  getLenis,
  initOnce,
  initPage,
  lenisStart,
  lenisStop,
  preparePage,
  resetScrollMotionState,
  refreshScrollTrigger,
} from './motion-engine';
import { runPageTransition } from './transition';

type ConsentServices = {
  weather: boolean;
  analytics: boolean;
  media: boolean;
};

type ConsentValue = {
  version: 2;
  decidedAt: string;
  services: ConsentServices;
};

const CONSENT_KEY = 'rh-cookie-consent';
let transitionActive = false;
let currentMain: HTMLElement | null = null;

function setNavStatus(active: boolean): void {
  document.body.setAttribute('data-navigation-status', active ? 'active' : 'not-active');
  const toggle = document.querySelector<HTMLButtonElement>('.bold-nav-full__hamburger');
  toggle?.setAttribute('aria-expanded', active ? 'true' : 'false');
  document.querySelector<HTMLElement>('.bold-nav-full__tile')?.setAttribute('aria-hidden', active ? 'false' : 'true');
}

function closeNav(): void {
  setNavStatus(false);
  lenisStart();
  document.body.style.overflow = '';
}

function toggleNav(): void {
  const next = document.body.getAttribute('data-navigation-status') !== 'active';
  setNavStatus(next);
  if (next) {
    lenisStop();
    document.body.style.overflow = 'hidden';
  } else {
    lenisStart();
    document.body.style.overflow = '';
  }
}

function resetScrollPosition(): void {
  const lenis = getLenis();
  if (lenis) {
    lenis.scrollTo(0, { immediate: true });
    lenis.resize();
    lenis.start();
  }
  window.scrollTo(0, 0);
  resetScrollMotionState(0);
}

function resetPage(container: HTMLElement): void {
  resetScrollPosition();
  gsap.set(container, {
    clearProps:
      'position,top,left,right,zIndex,maxHeight,height,overflow,opacity,visibility,clipPath,webkitClipPath,willChange,force3D',
  });
}

function freezeMain(main: HTMLElement): HTMLElement {
  const y = window.scrollY || window.pageYOffset;
  const clone = main.cloneNode(true) as HTMLElement;
  clone.removeAttribute('id');
  clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
  clone.querySelectorAll('.splatter, .orbit, .small-circles').forEach((element) => element.remove());
  clone.setAttribute('data-freeze', '');
  Object.assign(clone.style, {
    position: 'fixed',
    top: String(-y) + 'px',
    left: '0',
    right: '0',
    width: '100%',
    margin: '0',
    zIndex: '9',
    pointerEvents: 'none',
  });
  document.body.appendChild(clone);
  return clone;
}

function waitForMenuClose(done: () => void): void {
  const tile = document.querySelector<HTMLElement>('.bold-nav-full__tile');
  if (!tile) {
    done();
    return;
  }

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    tile.removeEventListener('transitionend', onEnd);
    window.clearTimeout(timer);
    done();
  };
  const onEnd = (event: TransitionEvent) => {
    if (event.target === tile && event.propertyName === 'clip-path') finish();
  };
  tile.addEventListener('transitionend', onEnd);
  const timer = window.setTimeout(finish, 1100);
}

function updateDocumentMetadata(parsed: Document, url: URL): void {
  document.title = parsed.title;
  ['meta[name="description"]', 'link[rel="canonical"]', 'link[rel="alternate"][hreflang]'].forEach(
    (selector) => {
      document.head.querySelectorAll(selector).forEach((element) => element.remove());
      parsed.head.querySelectorAll(selector).forEach((element) => {
        document.head.appendChild(element.cloneNode(true));
      });
    },
  );

  document.querySelectorAll<HTMLAnchorElement>('.site-header a[href]').forEach((link) => {
    const linkUrl = new URL(link.href, window.location.href);
    const active = linkUrl.pathname.replace(/\/+$/, '') === url.pathname.replace(/\/+$/, '');
    if (active) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  });
}

async function navigate(url: URL, pushState = true): Promise<void> {
  if (transitionActive || !currentMain) return;
  transitionActive = true;

  const start = async () => {
    let response: Response;
    try {
      response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'RAW-HUNGRY-Transition' },
      });
    } catch {
      window.location.assign(url.href);
      return;
    }

    if (!response.ok) {
      window.location.assign(url.href);
      return;
    }

    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const incoming = parsed.querySelector<HTMLElement>('main#primary');
    const incomingLocale = parsed.body.getAttribute('data-rh-locale');
    const currentLocale = document.body.getAttribute('data-rh-locale');
    if (!incoming || incomingLocale !== currentLocale) {
      window.location.assign(url.href);
      return;
    }

    document.querySelectorAll('main[data-freeze]').forEach((element) => element.remove());
    const outgoing = currentMain as HTMLElement;
    const frozen = freezeMain(outgoing);
    resetScrollPosition();
    cleanupPage(outgoing);
    outgoing.replaceWith(incoming);
    currentMain = incoming;
    preparePage(incoming);
    updateDocumentMetadata(parsed, url);

    if (pushState) window.history.pushState({ rhTransition: true }, '', url.href);

    runPageTransition(frozen, incoming, () => {
      resetPage(incoming);
      initPage(incoming, closeNav);
      window.setTimeout(refreshScrollTrigger, 100);
      transitionActive = false;
    });
  };

  if (document.body.getAttribute('data-navigation-status') === 'active') {
    closeNav();
    waitForMenuClose(() => void start());
  } else {
    await start();
  }
}

function readConsent(): ConsentValue | null {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CONSENT_KEY) || 'null');
    if (parsed?.version === 2 && parsed.services) return parsed as ConsentValue;
    if (typeof parsed?.analytics === 'boolean') {
      return {
        version: 2,
        decidedAt: parsed.decidedAt || new Date().toISOString(),
        services: { weather: true, analytics: parsed.analytics, media: false },
      };
    }
  } catch {
    // Storage can be unavailable in private browsing.
  }
  return null;
}

function applyConsent(services: ConsentServices): void {
  document.querySelectorAll<HTMLElement>('.footer-weather').forEach((element) => {
    element.style.display = services.weather ? '' : 'none';
  });
}

function setupCookieConsent(): void {
  const banner = document.querySelector<HTMLElement>('[data-rh-cookie-consent]');
  if (!banner) return;

  const settings = banner.querySelector<HTMLElement>('[data-rh-cookie-settings]');
  const defaultActions = banner.querySelector<HTMLElement>('[data-rh-cookie-actions-default]');
  const expandedActions = banner.querySelector<HTMLElement>('[data-rh-cookie-actions-expanded]');
  const inputs = Array.from(
    banner.querySelectorAll<HTMLInputElement>('input[data-rh-cookie-service]'),
  ).filter((input) => Boolean(input.dataset.rhCookieService));
  let hasDecision = readConsent() !== null;

  const selected = (): ConsentServices => ({
    weather: banner.querySelector<HTMLInputElement>('[data-rh-cookie-service="weather"]')?.checked ?? true,
    analytics: banner.querySelector<HTMLInputElement>('[data-rh-cookie-service="analytics"]')?.checked ?? false,
    media: banner.querySelector<HTMLInputElement>('[data-rh-cookie-service="media"]')?.checked ?? false,
  });

  const setExpanded = (expanded: boolean) => {
    settings?.classList.toggle('is-open', expanded);
    settings?.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    if (defaultActions) defaultActions.hidden = expanded;
    if (expandedActions) expandedActions.hidden = !expanded;
    inputs.forEach((input) => {
      input.disabled = !expanded;
    });
  };

  const open = () => {
    const stored = readConsent();
    if (stored) {
      inputs.forEach((input) => {
        const id = input.dataset.rhCookieService as keyof ConsentServices;
        input.checked = stored.services[id] === true;
      });
    }
    setExpanded(false);
    banner.hidden = false;
  };

  const persist = (services: ConsentServices) => {
    try {
      window.localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ version: 2, decidedAt: new Date().toISOString(), services }),
      );
    } catch {
      // The banner reappears next visit when storage is unavailable.
    }
    hasDecision = true;
    applyConsent(services);
    banner.hidden = true;
    setExpanded(false);
  };

  document.querySelectorAll<HTMLElement>('[data-rh-cookie-open]').forEach((button) => {
    button.addEventListener('click', open);
  });
  banner.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-rh-cookie-action]');
    const action = button?.dataset.rhCookieAction;
    if (!action) return;
    if (action === 'customize') setExpanded(true);
    if (action === 'back') setExpanded(false);
    if (action === 'save') persist(selected());
    if (action === 'all') persist({ weather: true, analytics: true, media: true });
    if (action === 'essential') persist({ weather: false, analytics: false, media: false });
  });

  const stored = readConsent();
  if (stored) {
    applyConsent(stored.services);
    banner.hidden = true;
  } else {
    open();
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !banner.hidden && hasDecision) {
      banner.hidden = true;
      setExpanded(false);
    }
  });
}

function setupNavigation(): void {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const hamburger = target.closest<HTMLButtonElement>('.bold-nav-full__hamburger');
    if (hamburger) {
      event.preventDefault();
      toggleNav();
      return;
    }

    const anchor = target.closest<HTMLAnchorElement>('a[href]');
    if (
      !anchor ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      anchor.target === '_blank' ||
      anchor.hasAttribute('download')
    ) {
      return;
    }

    const url = new URL(anchor.href, window.location.href);
    if (
      url.origin !== window.location.origin ||
      url.protocol !== window.location.protocol ||
      url.pathname.startsWith('/wp-admin/') ||
      url.pathname.startsWith('/wp-login.php')
    ) {
      return;
    }

    const currentPath = window.location.pathname.replace(/\/+$/, '');
    const targetPath = url.pathname.replace(/\/+$/, '');
    if (currentPath === targetPath && url.search === window.location.search) {
      if (url.hash) return;
      event.preventDefault();
      closeNav();
      return;
    }
    if (!/^\/(de|en)(\/|$)/.test(url.pathname)) return;

    event.preventDefault();
    void navigate(url);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.getAttribute('data-navigation-status') === 'active') {
      closeNav();
    }
  });

  window.addEventListener('popstate', () => {
    void navigate(new URL(window.location.href), false);
  });
}

function boot(): void {
  document.documentElement.classList.add('js');
  currentMain = document.querySelector<HTMLElement>('main#primary');
  initOnce();
  if (currentMain) {
    resetPage(currentMain);
    preparePage(currentMain);
    initPage(currentMain, closeNav);
  }
  setupNavigation();
  setupCookieConsent();

  window.addEventListener('elementor/frontend/init', () => {
    const main = document.querySelector<HTMLElement>('main#primary');
    if (!main) return;
    cleanupPage(currentMain);
    currentMain = main;
    preparePage(main);
    initPage(main, closeNav);
    window.setTimeout(refreshScrollTrigger, 100);
  });

  window.RH_MOTION = {
    reinitialize() {
      const main = document.querySelector<HTMLElement>('main#primary');
      if (!main) return;
      cleanupPage(currentMain);
      currentMain = main;
      preparePage(main);
      initPage(main, closeNav);
      refreshScrollTrigger();
    },
  };
}

declare global {
  interface Window {
    RH_MOTION: { reinitialize: () => void };
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
